import React, { useMemo } from 'react';
import { AnyfeedDataProps } from 'types/unacademyIcons/anyfeed.type';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import FlexRow from '@unicons-base/FlexRow/FlexRow';
import Typography from '@unicons-base/Typography/Typography';
import UN_ICONS_TYPOGRAPHY_VARIANT from 'types/unacademyIcons/typography.type';
import Image from '@base/Image';
import useWindowSize from '@hooks/useWindowSize';

const StyledFlexRow = styled(FlexRow)`
  ${mediaQuery.uptoTablet} {
    padding: 0px ${Spacings.SPACING_2B};
  }
`;

const StyledColumn = styled.div`
  padding: ${Spacings.SPACING_2B};
  flex: 0 0 25%;
  max-width: 25%;
  ${mediaQuery.uptoIconTablet} {
    padding: ${Spacings.SPACING_6};
  }
  ${mediaQuery.uptoTablet} {
    flex: 50%;
    max-width: 50%;
    padding: ${Spacings.SPACING_3B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_6};
  }
`;

const ContentWrapper = styled.div`
  background: var(--color-i-icon-dark-primary);
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_6B};
  border-radius: 8px;
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_3B};
  }
`;

const ListTitle = styled(Typography)`
  text-align: center;
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H5};
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
  }
`;

const ListDescription = styled(Typography)`
  text-align: center;
  color: var(--color-i-icon-dark-secondary);
  line-height: 22px;
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H6};
    line-height: 16px;
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H7};
    line-height: 16px;
  }
`;

const StyledIcon = styled.div`
  background-color: var(--color-i-icon-dark-supporting-secondary);
  border-radius: 100%;
  padding: 20px;
  display: flex;
  justify-content: center;
  height: 80px;
  width: 80px;
  margin: auto;
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    width: 56px;
    height: 56px;
    padding: 8px;
    margin-bottom: ${Spacings.SPACING_5B};
  }
`;

const AnyfeedList: React.FC<AnyfeedDataProps> = ({ data, list }) => {
  const { items } = list?.[0];
  const { isMobile } = useWindowSize();
  const imageSize = useMemo(() => (isMobile ? 32 : 40), [isMobile]);
  return (
    <StyledFlexRow>
      {items.map((item, index) => (
        <StyledColumn key={String(index)}>
          <ContentWrapper>
            <StyledIcon>
              <Image
                src={item.icon}
                alt={item.title}
                width={imageSize}
                height={imageSize}
                layout="intrinsic"
              />
            </StyledIcon>
            <ListTitle variant={UN_ICONS_TYPOGRAPHY_VARIANT.H3}>
              {item.title}
            </ListTitle>
            <ListDescription variant={UN_ICONS_TYPOGRAPHY_VARIANT.SUBTITLE2}>
              {item.description}
            </ListDescription>
          </ContentWrapper>
        </StyledColumn>
      ))}
    </StyledFlexRow>
  );
};

AnyfeedList.displayName = 'AnyfeedList';

export default AnyfeedList;
